import { useId, type ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { Skin } from '#src/__generated__/globalTypes.ts';

type GradientConfig = {
  coords: { x1: string; y1: string; x2: string; y2: string };
  stops: ReactNode;
};

const GRADIENTS: Partial<Record<Skin, GradientConfig>> = {
  [Skin.SHINY]: {
    coords: { x1: '23.3237', y1: '41.9668', x2: '44.0388', y2: '15.2986' },
    stops: (
      <>
        <stop stopColor="#EAE3DA" />
        <stop offset="0.283178" stopColor="#BFE6EF" />
        <stop offset="0.623627" stopColor="#E1E3F5" />
        <stop offset="0.811813" stopColor="#E6BADD" />
        <stop offset="1" stopColor="#E8F6EF" />
      </>
    ),
  },
  [Skin.HOLO]: {
    coords: { x1: '62.8371', y1: '49.4997', x2: '46.2458', y2: '14.4602' },
    stops: (
      <>
        <stop stopColor="#FEF314" />
        <stop offset="1" stopColor="#FF4E22" />
      </>
    ),
  },
  [Skin.FULL_ART]: {
    coords: { x1: '69.5331', y1: '1.13221', x2: '38.5272', y2: '64.1775' },
    stops: (
      <>
        <stop stopColor="white" />
        <stop offset="0.352177" stopColor="#FCFF63" />
        <stop offset="1" stopColor="#C26900" />
      </>
    ),
  },
};

const STANDARD_FILL = 'white';

type Props = { power: number; skin: Skin };
export const SetCardBonus = ({ power, skin }: Props) => {
  const id = useId();
  const gradient = GRADIENTS[skin];
  const fillValue = gradient ? `url(#${id})` : STANDARD_FILL;

  if (power === 1) {
    return null;
  }

  const percent = power - 1;

  return (
    <svg
      width="80"
      height="44"
      viewBox="-20 0 80 44"
      fill="none"
      overflow="visible"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-labelledby={`${id}-title`}
      style={{ fontFamily: 'var(--pressio-font)' }}
    >
      <title id={`${id}-title`}>
        <FormattedMessage
          defaultMessage="{value, number, percent} bonus"
          description="Set card bonus aria label"
          values={{ value: percent }}
        />
      </title>

      <defs>
        {gradient ? (
          <linearGradient
            id={id}
            {...gradient.coords}
            gradientUnits="userSpaceOnUse"
          >
            {gradient.stops}
          </linearGradient>
        ) : null}

        <mask id={`${id}-mask`}>
          <rect x="0" y="0" width="219" height="140" fill="black" />
          <path
            d="M16.1123 1.04687C18.5188 -0.348897 21.4836 -0.349004 23.8887 1.04687L36.1133 8.14062C38.5183 9.53662 40 12.1165 40 14.9082V29.0928C40 31.8846 38.5183 34.4644 36.1133 35.8603L23.8887 42.9531C21.4821 44.3491 18.5175 44.3491 16.1123 42.9531L3.8877 35.8603C1.48248 34.4644 2.02939e-06 31.8847 1.90735e-06 29.0928V14.9082C1.78531e-06 12.1163 1.48254 9.53658 3.8877 8.14062L16.1123 1.04687Z"
            fill="white"
          />
          <text
            x={percent < 0.1 ? '-15' : '-20'}
            y="20"
            textAnchor="middle"
            dominantBaseline="central"
            fill="white"
            fontSize="16"
            fontWeight="500"
            fontStyle="normal"
            letterSpacing="-0.32"
            stroke="white"
            strokeWidth="4"
            paintOrder="stroke"
          >
            +
          </text>
          <text
            x="20"
            y="15"
            textAnchor="middle"
            dominantBaseline="central"
            fill="white"
            fontSize="32"
            fontWeight="500"
            fontStyle="normal"
            letterSpacing="-0.64"
            stroke="white"
            strokeWidth="4"
          >
            <FormattedNumber value={percent} style="percent" />
          </text>
          <text
            x="20"
            y="36"
            textAnchor="middle"
            dominantBaseline="central"
            fill="white"
            fontSize="12"
            fontWeight="500"
            fontStyle="normal"
            letterSpacing="-0.24"
            stroke="white"
            strokeWidth="4"
            style={{ textTransform: 'uppercase' }}
          >
            <FormattedMessage
              defaultMessage="Bonus"
              description="Set card bonus"
            />
          </text>
        </mask>
      </defs>

      <rect
        mask={`url(#${id}-mask)`}
        x="-40"
        y="0"
        width="100"
        height="100"
        fill={fillValue}
      />
      <text
        x={percent < 0.1 ? '-15' : '-20'}
        y="20"
        textAnchor="middle"
        dominantBaseline="central"
        fill="black"
        fontSize="16"
        fontWeight="500"
        fontStyle="normal"
        letterSpacing="-0.32"
        stroke="transparent"
        strokeWidth="4"
      >
        +
      </text>
      <text
        x="20"
        y="15"
        textAnchor="middle"
        dominantBaseline="central"
        fill="black"
        fontSize="32"
        fontWeight="500"
        fontStyle="normal"
        letterSpacing="-0.64"
        stroke="transparent"
        strokeWidth="4"
      >
        <FormattedNumber value={percent} style="percent" />
      </text>
      <text
        x="20"
        y="36"
        textAnchor="middle"
        dominantBaseline="central"
        fill="black"
        fontSize="12"
        fontWeight="500"
        fontStyle="normal"
        letterSpacing="-0.24"
        stroke="transparent"
        strokeWidth="4"
        style={{ textTransform: 'uppercase' }}
      >
        <FormattedMessage defaultMessage="Bonus" description="Set card bonus" />
      </text>
    </svg>
  );
};
